import { getStore } from '../../../helpers/initStore.js';
import { getPatientShipmentDisabledOnOffice, getRequiredSuppliesFromProcedures, filterNursesWithSupplies, getCheapestPatientShipmentInfo, getLatestSupplyArrivalTimestamp, getSupplyArrivalTimeError } from './helpers/supply.js';
import { getTimeSlotDebugInfo } from './helpers/utilities.js';
import { getIsDebugMode } from '../../variables/helpers.js';

/**
 * Filter time slots by CareSend-shipped supply constraints.
 * 1. Split the time slots into eligible and ineligible based on patient shipment info
 * 2. Check the patient shipment info for each time slot to determine if the time slot is eligible
 * 3. For all ineligible time slots, check if a nurse has all of the required supplies to fulfill
 */
const getCareSendSupplyFilteredTimeSlots = (timeSlotsByDate, patientShipmentInfo, supplyInstructions, draftWaypoint, procedureIDs, warehouseFallback) => {
    const store = getStore();
    const isDebugMode = getIsDebugMode();
    const timeSlotsByDateObj = {};
    const timeSlotIDsEligibleByNurse = [];
    const cheapestPatientShipmentInfoByTime = {};
    const patientShipmentDisabledByOffice = getPatientShipmentDisabledOnOffice(draftWaypoint);
    const requiredSupplies = getRequiredSuppliesFromProcedures(procedureIDs, supplyInstructions);
    Object.entries(timeSlotsByDate).map(([date, timeSlotsByTime]) => {
        Object.entries(timeSlotsByTime).map(([time, timeSlot]) => {
            var _a;
            if (timeSlot.disabled) {
                timeSlotsByDateObj[date] = {
                    ...timeSlotsByDateObj[date],
                    [time]: timeSlot,
                };
                return;
            }
            const failures = [];
            const slot = timeSlot;
            // We first check if any of the eligible nurses have the required supplies
            // because they have priority over using patient shipments.
            const nursesWithSupplies = filterNursesWithSupplies(slot.availableNurses, requiredSupplies);
            const anyNursesWithSupplies = Object.keys(nursesWithSupplies).length > 0;
            if (anyNursesWithSupplies) {
                slot.availableNurses = nursesWithSupplies;
                timeSlotIDsEligibleByNurse.push(time);
            }
            else if (patientShipmentDisabledByOffice) {
                failures.push('Patient Shipment Disabled by Office');
            }
            else {
                const cheapestPatientShipmentInfo = getCheapestPatientShipmentInfo(patientShipmentInfo, time, draftWaypoint, warehouseFallback, requiredSupplies);
                if (cheapestPatientShipmentInfo) {
                    cheapestPatientShipmentInfoByTime[time] = cheapestPatientShipmentInfo;
                }
                else {
                    failures.push('No eligible patient shipment info');
                }
            }
            const timeSlotsForDate = (_a = timeSlotsByDateObj[date]) !== null && _a !== void 0 ? _a : {};
            if (!failures.length)
                timeSlotsForDate[time] = slot;
            if (failures.length && isDebugMode)
                timeSlotsForDate[time] = getTimeSlotDebugInfo(slot, failures);
            timeSlotsByDateObj[date] = timeSlotsForDate;
        });
    });
    store.commit('schedulingFlow/SET_CHEAPEST_PATIENT_SHIPMENT_INFO', cheapestPatientShipmentInfoByTime);
    store.commit('schedulingFlow/SET_TIME_SLOT_IDS_ELIGIBLE_BY_NURSE', timeSlotIDsEligibleByNurse);
    return timeSlotsByDateObj;
};
/**
 * Filter time slots by office-shipped supply constraints.
 * 1. Ensure that the shipment of supplies sent by the office is before the
 *    time slot start time.
 */
const getOfficeSupplyFilteredTimeSlots = (timeSlotsByDate, supplyInstructions) => {
    const isDebugMode = getIsDebugMode();
    const timeSlotsByDateObj = {};
    const latestSupplyArrivalTimestamp = getLatestSupplyArrivalTimestamp(supplyInstructions);
    Object.entries(timeSlotsByDate).map(([date, timeSlotsByTime]) => {
        Object.entries(timeSlotsByTime).map(([time, timeSlot]) => {
            var _a;
            if (timeSlot.disabled) {
                timeSlotsByDateObj[date] = {
                    ...timeSlotsByDateObj[date],
                    [time]: timeSlot,
                };
                return;
            }
            const error = getSupplyArrivalTimeError(timeSlot, latestSupplyArrivalTimestamp);
            const timeSlotsForDate = (_a = timeSlotsByDateObj[date]) !== null && _a !== void 0 ? _a : {};
            if (!error)
                timeSlotsForDate[time] = timeSlot;
            if (error && isDebugMode)
                timeSlotsForDate[time] = getTimeSlotDebugInfo(timeSlot, [error]);
            timeSlotsByDateObj[date] = timeSlotsForDate;
        });
    });
    return timeSlotsByDateObj;
};

export { getCareSendSupplyFilteredTimeSlots, getOfficeSupplyFilteredTimeSlots };
